import clsx from 'clsx'
import { format, utcToZonedTime } from 'date-fns-tz'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Spinner from '@/components-latest/primitives/Spinner'
import Tag from '@/components-latest/primitives/Tag'
import Text from '@/components-latest/primitives/Text'
import { IBillingCycle } from '@/ducks/billing/billing.types'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './CycleInfo.module.scss'

export type IBillingCycleTag =
  | 'PAID'
  | 'UNPAID'
  | 'RECEIVED'
  | 'UNRECEIVED'
  | null

const formatTimestampToCycleDate = (timestamp?: number) => {
  if (!timestamp) return null
  return format(utcToZonedTime(timestamp, 'UTC'), 'MMM d, yyyy')
}

type Props = {
  cycle: IBillingCycle
  isLoading?: boolean
  type: 'paid' | 'unpaid'
  className?: string
}
const CycleInfo = ({ cycle, isLoading, type, className }: Props) => {
  const { t } = useTranslation()
  const currency = useSelector(selectCurrencyCode) || ''

  let tagType: IBillingCycleTag = null
  let dueText = ''
  const earnings = cycle?.billings?.final_admin_earnings as number

  // TODO: rid of extra branch or fix switch statement
  switch (true) {
    case !earnings: {
      tagType = null
      dueText = t('billing.cycle.paid-due')
      break
    }
    case earnings > 0: {
      tagType = type === 'paid' ? 'RECEIVED' : 'UNRECEIVED'
      dueText =
        type === 'paid'
          ? t('billing.cycle.received-due')
          : t('billing.cycle.due-to-receive')
      break
    }
    case earnings < 0: {
      tagType = type === 'paid' ? 'PAID' : 'UNPAID'
      dueText =
        type === 'paid'
          ? t('billing.cycle.paid-due')
          : t('billing.cycle.due-to-pay')
      break
    }
    default: {
      break
    }
  }

  if (!cycle || isLoading)
    return (
      <div className={Styles.spinner}>
        <Spinner />
      </div>
    )

  return (
    <div className={clsx(Styles.wrapper, className)}>
      <div className={Styles.header}>
        <Text asChild weight="semiBold" variant="body">
          <p>
            {formatTimestampToCycleDate(cycle.details.start_time)} -{' '}
            {formatTimestampToCycleDate(cycle.details.end_time)}
          </p>
        </Text>
        {tagType ? (
          <Tag
            className={clsx(Styles.baseTag, {
              [Styles.paid]: tagType === 'PAID',
              [Styles.received]: tagType === 'RECEIVED',
              [Styles.unpaid]: tagType === 'UNPAID',
              [Styles.unreceived]: tagType === 'UNRECEIVED'
            })}>
            {t(`billing.cycle.${tagType}`)}
          </Tag>
        ) : null}
      </div>
      <div className={Styles.finances}>
        <div className={Styles.total}>
          <Text variant="nextSubtitle" weight="extraBold">
            {formatPrice(
              cycle?.billings?.total_restaurant_earnings || 0,
              currency,
              {
                signDisplay: 'auto',
                minimumFractionDigits: 0
              }
            )}
          </Text>
          <Text className={Styles.financeDesc}>
            {t('billing.cycle.total-profit')}
          </Text>
        </div>
        <div className={Styles.due}>
          <Text
            className={clsx({
              [Styles.pink]: tagType === 'UNPAID',
              [Styles.skyblue]: tagType === 'UNRECEIVED'
            })}
            variant="nextSubtitle"
            weight="extraBold">
            {formatPrice(cycle?.billings?.final_admin_earnings || 0, currency, {
              signDisplay: 'auto',
              minimumFractionDigits: 0
            })}
          </Text>
          <Text className={Styles.financeDesc}>{dueText}</Text>
        </div>
      </div>
    </div>
  )
}

export default CycleInfo
